import React from 'react';
import type { SettlementStatement, VenueSettlementSummary } from '../../api/client';

function centsToDollars(cents: number): string {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatPeriod(startIso: string, endIso: string, cadence: 'weekly' | 'monthly'): string {
  const start = new Date(startIso.slice(0, 10) + 'T12:00:00Z');
  if (cadence === 'weekly') {
    const inclusiveEnd = new Date(new Date(endIso.slice(0, 10) + 'T12:00:00Z').getTime() - 86_400_000);
    return `${start.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' })}–${inclusiveEnd.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' })}`;
  }
  return start.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', year: 'numeric' });
}

type PlatformSettlementPanelProps = {
  settlement: VenueSettlementSummary | null;
  statements?: SettlementStatement[];
};

export default function PlatformSettlementPanel({ settlement, statements = [] }: PlatformSettlementPanelProps) {
  // settlement may be null when the summary fetch fails — still render the
  // header/copy so a platform-collect venue never falls through to the
  // (misleading) Stripe Connect card, just without the numbers.
  const pending = settlement?.pending ?? null;
  const cadence = settlement?.settlementCadence === 'weekly' ? 'weekly' : 'monthly';
  const cadenceLabel = cadence === 'weekly' ? 'Weekly' : 'Monthly';
  const cadenceTiming = cadence === 'weekly' ? 'each week' : 'at the end of each month';
  const lastSettlement = settlement?.lastSettlement ?? null;
  const oldestLabel = pending?.oldestEntryAt
    ? new Date(pending.oldestEntryAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  const lastSettledLabel = lastSettlement?.settled_at
    ? new Date(lastSettlement.settled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '20px 24px', flexWrap: 'wrap', gap: '12px',
        borderBottom: '1px solid var(--gray-200)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--gray-900)' }}>
              {cadenceLabel} Settlement
            </h3>
            <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: 4 }}>
              PickleCourts collects customer payments on each booking and rolls up your net earnings {cadenceTiming}.
            </div>
          </div>
        </div>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '6px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 600,
          background: '#e0f2fe', color: '#075985', border: '1px solid #bae6fd',
        }}>
          Platform-collect
        </div>
      </div>

      <div style={{ padding: '24px' }}>
        {!pending && (
          <div style={{ fontSize: '13px', color: 'var(--gray-500)' }}>
            Settlement details are temporarily unavailable. Your balance remains recorded for the next {cadence} rollup.
          </div>
        )}
        {pending && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '24px',
          padding: '16px 20px', background: 'var(--gray-50)', borderRadius: '10px',
        }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
              Pending owed
            </div>
            <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--gray-900)' }}>
              {centsToDollars(pending.netToVenueCentsTotal)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
              Entries
            </div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--gray-900)' }}>
              {pending.entryCount}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
              Gross collected
            </div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--gray-900)' }}>
              {centsToDollars(pending.grossCentsTotal)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
              Commission
            </div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--gray-900)' }}>
              {centsToDollars(pending.commissionCentsTotal)}
            </div>
          </div>
          {oldestLabel && (
            <div>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                Oldest pending
              </div>
              <div style={{ fontSize: '13px', color: 'var(--gray-700)' }}>{oldestLabel}</div>
            </div>
          )}
          {lastSettledLabel && (
            <div>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                Last settled
              </div>
              <div style={{ fontSize: '13px', color: 'var(--gray-700)' }}>
                {lastSettledLabel}
                {lastSettlement?.settlement_reference ? ` - ${lastSettlement.settlement_reference}` : ''}
              </div>
            </div>
          )}
        </div>
        )}

        {statements.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <div style={{
              fontSize: '13px', fontWeight: 600, color: 'var(--gray-900)', marginBottom: '8px',
            }}>
              Transfer history
            </div>
            <div style={{ overflowX: 'auto', border: '1px solid var(--gray-200)', borderRadius: '10px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)' }}>
                    {['Period', 'Bookings', 'Gross', 'Commission', 'Net Transfer', 'Status', 'Paid', 'Reference'].map(h => (
                      <th key={h} style={{
                        padding: '8px 12px', textAlign: 'left', fontWeight: 600,
                        color: 'var(--gray-500)', whiteSpace: 'nowrap', fontSize: '11px',
                        textTransform: 'uppercase', letterSpacing: '0.5px',
                        borderBottom: '1px solid var(--gray-200)',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {statements.map((st, i) => (
                    <tr key={st.id} style={{ borderBottom: i < statements.length - 1 ? '1px solid var(--gray-100)' : 'none' }}>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontWeight: 500 }}>{formatPeriod(st.period_start, st.period_end, cadence)}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--gray-500)' }}>{st.entry_count}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{centsToDollars(st.gross_cents)}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: 'var(--gray-400)', fontVariantNumeric: 'tabular-nums' }}>{centsToDollars(st.commission_cents)}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontWeight: 600, color: '#15803d', fontVariantNumeric: 'tabular-nums' }}>{centsToDollars(st.net_to_venue_cents)}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{
                          display: 'inline-flex', padding: '2px 8px', borderRadius: 999,
                          fontSize: 11, fontWeight: 700,
                          background: st.status === 'paid' ? '#dcfce7' : '#fef3c7',
                          color: st.status === 'paid' ? '#15803d' : '#92400e',
                          border: `1px solid ${st.status === 'paid' ? '#bbf7d0' : '#fde68a'}`,
                        }}>
                          {st.status === 'paid' ? 'Paid' : 'Finalized'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: 'var(--gray-500)' }}>
                        {st.paid_at ? formatDay(st.paid_at) : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--gray-400)', fontSize: '12px', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={st.payment_reference || undefined}>
                        {st.payment_reference || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
